import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './PersonPicker.css';

/**
 * Şəxs seçimi — ad yazaraq axtarmaq üçün.
 * Adi <select> axtarışı dəstəkləmir, siyahı uzun olanda isə tapmaq çətinləşir.
 *
 * props:
 *   value     — seçilmiş id (və ya '')
 *   people    — [{ id, name, position }]
 *   onChange  — (id | '') qaytarır
 *   placeholder, emptyLabel
 */
export default function PersonPicker({ value, people = [], onChange, placeholder, emptyLabel, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hl, setHl] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => people.find(p => String(p.id) === String(value)) || null,
    [people, value]
  );

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return people.slice(0, 80);
    return people
      .filter(p => (p.name || '').toLowerCase().includes(s) || (p.position || '').toLowerCase().includes(s))
      .sort((a, b) => {
        const as = (a.name || '').toLowerCase().startsWith(s) ? 0 : 1;
        const bs = (b.name || '').toLowerCase().startsWith(s) ? 0 : 1;
        return as - bs;
      })
      .slice(0, 80);
  }, [q, people]);

  useEffect(() => { setHl(0); }, [q]);

  // Siyahı document.body-yə portal ilə qoyulur: modalın overflow-u onu kəsirdi.
  // Yeri hər dəfə sahənin real koordinatından hesablanır, yer çatmasa yuxarı açılır.
  const [pos, setPos] = useState(null);
  const dropRef = useRef(null);

  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom - 10;
    const spaceAbove = r.top - 10;
    const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    setPos({
      left: r.left,
      width: r.width,
      maxH: Math.max(180, Math.min(340, flipUp ? spaceAbove : spaceBelow)),
      top: flipUp ? undefined : r.bottom + 5,
      bottom: flipUp ? vh - r.top + 5 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    reposition();
    window.addEventListener('scroll', reposition, true);   // modal daxili scroll da tutulsun
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    const onDown = e => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (open && pos && inputRef.current) inputRef.current.focus();
  }, [open, pos]);

  const choose = (p) => { onChange(p ? String(p.id) : ''); setOpen(false); setQ(''); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); setHl(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHl(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); if (matches[hl]) choose(matches[hl]); }
    else if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div className="pp-wrap" ref={wrapRef}>
      <button
        type="button"
        className={'pp-trigger' + (selected ? ' has' : '')}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        <span className="pp-value">
          {selected
            ? <><b>{selected.name}</b>{selected.position && <small>{selected.position}</small>}</>
            : <span className="pp-empty">{emptyLabel}</span>}
        </span>
        {selected && !disabled && (
          <span className="pp-clear" role="button" tabIndex={-1}
                onClick={e => { e.stopPropagation(); choose(null); }}>
            <Icon name="close" size={13} />
          </span>
        )}
        <Icon name="search" size={14} />
      </button>

      {open && pos && createPortal(
        <div
          className="pp-drop"
          ref={dropRef}
          style={{
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxH,
            ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
          }}
        >
          <div className="pp-search">
            <Icon name="search" size={14} />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
            />
          </div>

          <div className="pp-list">
            <button type="button" className="pp-opt pp-opt-none" onClick={() => choose(null)}>
              {emptyLabel}
            </button>
            {matches.length === 0 ? (
              <div className="pp-none">Tapılmadı</div>
            ) : matches.map((p, i) => (
              <button
                type="button"
                key={p.id}
                className={'pp-opt' + (i === hl ? ' hl' : '') + (String(p.id) === String(value) ? ' sel' : '')}
                onMouseEnter={() => setHl(i)}
                onClick={() => choose(p)}
              >
                <span className="pp-name">{p.name}</span>
                {p.position && <span className="pp-pos">{p.position}</span>}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
